import classnames from 'classnames/bind';

import { PipelineTemplateSummary } from 'types/pipeline_template';

import { isIconType } from 'types/icon';

import { Button } from 'components/Button/Button';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { Text } from 'components/Text/Text';
import { PricingPlanLabel } from 'pipelines/components/PricingPlanLabel/PricingPlanLabel';

import { PipelineTemplateCardSkeleton } from './PipelineTemplateCardSkeleton';
import styles from './PipelineTemplateCard.module.scss';

const c = classnames.bind(styles);

export type PipelineTemplateCardProps = {
  template: PipelineTemplateSummary;
  isLoading?: boolean;
  showCreateButton?: boolean;
  showDeployButton?: boolean;
  onClick: (template: PipelineTemplateSummary) => void;
  onDeploy?: (template: PipelineTemplateSummary) => void;
};

export function PipelineTemplateCard({
  template,
  isLoading,
  showCreateButton,
  showDeployButton,
  onClick,
  onDeploy,
}: PipelineTemplateCardProps) {
  const isIncludedInPlan = template.is_included_in_plan !== false;
  const isCardClickable = Boolean(
    !onDeploy && showCreateButton && isIncludedInPlan
  );

  function handleCardClick() {
    if (!isCardClickable || isLoading) return;
    onClick(template);
  }

  function handleImportClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (!isIncludedInPlan) return;
    onClick(template);
  }

  function handleDeployClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (!isIncludedInPlan) return;
    onDeploy?.(template);
  }

  return (
    <article
      className={c('wrap', {
        clickable: isCardClickable,
        loading: isLoading,
        'upgrade-required': !isIncludedInPlan,
      })}
      onClick={isCardClickable ? handleCardClick : undefined}
      role={isCardClickable ? 'button' : undefined}
      aria-label={isCardClickable ? `Import ${template.name}` : undefined}
    >
      <div className={c('content')}>
        <div className={c('template')}>
          {template.categories.length > 0 && (
            <div className={c('template-categories', 'flex flex-wrap gap-2')}>
              {template.categories.map(({ label, icon_name }) => (
                <Text
                  className={c('flex items-center')}
                  size="small"
                  key={label}
                  inline
                >
                  {isIconType(icon_name) && (
                    <Icon name={icon_name} size="small" />
                  )}
                  <span>{label}</span>
                </Text>
              ))}
            </div>
          )}

          <Heading level="4" className={c('template-name')}>
            {template.name}
          </Heading>

          <div className={c('template-img-wrap')}>
            {template.gallery_img_url ? (
              <img
                className={c('template-img')}
                src={template.gallery_img_url}
                alt=""
              />
            ) : (
              <div className={c('template-img')}>Preview unavailable</div>
            )}

            {isLoading && (
              <div className="loading-spinner-wrap">
                <span className="loading-spinner" />
              </div>
            )}

            {!isIncludedInPlan && (
              <PricingPlanLabel
                className={c('template-plan-label')}
                placement="top-right"
                hasLink
              />
            )}
          </div>

          {template?.description && (
            <p className={c('template-description')}>{template.description}</p>
          )}
        </div>

        <div className={c('actions', 'mt-4 flex gap-2')}>
          {showDeployButton && (
            <Button
              variant="primary"
              id={`${String(template.id)}-deploy`}
              onClick={handleDeployClick}
              aria-label={`Deploy ${template.name}`}
              disabled={isLoading || !isIncludedInPlan}
              size="small"
            >
              Deploy
            </Button>
          )}
          {showCreateButton && (
            <Button
              variant="secondary"
              id={String(template.id)}
              onClick={handleImportClick}
              aria-label={`Import ${template.name}`}
              disabled={isLoading || !isIncludedInPlan}
              size="small"
            >
              Import
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

export { PipelineTemplateCardSkeleton };
